// Two-bone IK solver. Closed-form law-of-cosines.
//
// Given a hip joint, a foot target, and a pole hint, solve for the knee
// position such that ||hip - knee|| = upperLength and ||knee - foot|| =
// lowerLength, with the knee bending toward the pole hint.
//
// Returns positions only. Bone rotations are the caller's job — they
// require knowing the bone's rest pose orientation, which is a rendering
// concern, not a math concern. Keeping this purely geometric makes it
// trivially testable and basis-independent.
//
// Reference: ozz-animation `samples/two_bone_ik/sample_two_bone_ik.cc`.

export type Vec3 = readonly [number, number, number];

export interface TwoBoneIKResult {
  kneePos: Vec3;
  /** False when the target is beyond reach (>L1+L2) or inside |L1-L2|. */
  reachable: boolean;
}

const EPS = 1e-5;

function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function lengthSq(v: Vec3): number {
  return v[0] * v[0] + v[1] * v[1] + v[2] * v[2];
}

function normalizeOr(v: Vec3, fallback: Vec3): Vec3 {
  const lenSq = lengthSq(v);
  if (lenSq < EPS * EPS) return fallback;
  const inv = 1 / Math.sqrt(lenSq);
  return [v[0] * inv, v[1] * inv, v[2] * inv];
}

export function solveTwoBoneIK(
  hipPos: Vec3,
  target: Vec3,
  poleHint: Vec3,
  upperLength: number,
  lowerLength: number,
): TwoBoneIKResult {
  const toTarget = sub(target, hipPos);
  const dRaw = Math.sqrt(lengthSq(toTarget));

  const dMax = upperLength + lowerLength;
  const dMin = Math.abs(upperLength - lowerLength);
  const reachable = dRaw <= dMax && dRaw >= dMin;

  // Clamp to reachable range with small epsilon margin so the law-of-
  // cosines never sees a degenerate triangle.
  const d = Math.min(Math.max(dRaw, dMin + EPS), dMax - EPS);

  // Forward = direction hip → target. Falls back to the pole direction
  // when hip and target coincide (degenerate but legal).
  const forward: Vec3 =
    dRaw < EPS
      ? normalizeOr(sub(poleHint, hipPos), [1, 0, 0])
      : [toTarget[0] / dRaw, toTarget[1] / dRaw, toTarget[2] / dRaw];

  // Law of cosines for the angle at the hip between (hip→target) and
  // (hip→knee). cos α = (L1² + d² − L2²) / (2 L1 d).
  const cosAlpha =
    (upperLength * upperLength + d * d - lowerLength * lowerLength) /
    (2 * upperLength * d);
  const alphaClamp = Math.min(1, Math.max(-1, cosAlpha));
  const along = upperLength * alphaClamp;
  const lateral = upperLength * Math.sqrt(Math.max(0, 1 - alphaClamp * alphaClamp));

  // Lateral direction = pole hint projected onto the plane perpendicular
  // to forward. If the pole is colinear with forward, pick any
  // perpendicular fallback so we never produce a NaN.
  const toPole = sub(poleHint, hipPos);
  const poleAlong = dot(toPole, forward);
  const poleProj: Vec3 = [
    toPole[0] - forward[0] * poleAlong,
    toPole[1] - forward[1] * poleAlong,
    toPole[2] - forward[2] * poleAlong,
  ];

  let lateralDir = normalizeOr(poleProj, [0, 0, 0]);
  if (lengthSq(lateralDir) < EPS) {
    const fallback: Vec3 = Math.abs(forward[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
    const fAlong = dot(fallback, forward);
    lateralDir = normalizeOr(
      [
        fallback[0] - forward[0] * fAlong,
        fallback[1] - forward[1] * fAlong,
        fallback[2] - forward[2] * fAlong,
      ],
      [0, 1, 0],
    );
  }

  const kneePos: Vec3 = [
    hipPos[0] + forward[0] * along + lateralDir[0] * lateral,
    hipPos[1] + forward[1] * along + lateralDir[1] * lateral,
    hipPos[2] + forward[2] * along + lateralDir[2] * lateral,
  ];

  return { kneePos, reachable };
}
